// The masterclass, /masterclass (Masterclass.dc.html; CLAUDE.md type 4; decision 32): the video only,
// with how far they've watched, the questions line, the educational disclaimer under the video, and
// under it the Profit On Your Terms offer, the owner's card, or nothing.
//
// - The video is the shared player (src/product/Player.jsx: Vimeo's own player from npm, resume,
//   watched at the product's share), behind the board's orange cover until Play, so nothing loads
//   from Vimeo for someone who only looks.
// - "% watched" counts the furthest point reached, as the Library's tile does.
// - The offer comes only from hub.my_offer_state and fails closed: nothing shows there until the
//   database has answered, and nothing when it errs. The offer is on and they own no POYT: the
//   placeholder until the player passes the owner's minute (1:35:00), the video ends, or "Show it now";
//   then the offer, written straight to member_product_state.offer_reached_at so it stays shown on
//   every device. The owner's card for someone who has paid POYT in full; nothing at all for a
//   secured place or with the offer off.
// - n8n's 2 events go through masterclass-event, from the server: play the first time the video
//   starts and offer once it's reached and shown, each only while not sent for them yet. Only the
//   video at /masterclass sends them (model.js sendsWatchEvents): any other masterclass sends nothing,
//   and doesn't read whether they were sent.
// While staff view as a member nothing is written and nothing is sent.
import { useEffect, useRef, useState } from 'react'
import * as f from '../../lib/format.js'
import { useMember } from '../../app/member.jsx'
import { usePhone } from '../../lib/media.js'
import { useViewingAs } from '../../lib/viewAs.jsx'
import { SITE } from '../../config/site.js'
import { useProductData } from '../../product/ProductData.jsx'
import { RecordingPlayer, vimeoOf } from '../../product/Player.jsx'
import { loadRecordingContent, saveProgress } from '../../product/data.js'
import { progressFor, progressPayload } from '../../product/model.js'
import { COPY } from './copy.js'
import { coverLabel, firstVisit, offerArea, progressView, revealSeconds, sendsWatchEvents } from './model.js'
import { loadOfferState, loadSent, markReached, sendEvent } from './data.js'
import { OfferBlock, OfferPlaceholder, OwnerCard } from './Offer.jsx'
import '../../styles/masterclass.css'

function PlayDisc() {
  return (
    <span className="mc-disc" aria-hidden="true">
      <svg width="44" height="44" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
    </span>
  )
}

// A read that may still be on its way: { status: 'loading' | 'ready' | 'error', row }
function useRead(load, deps) {
  const [s, set] = useState({ status: 'loading', row: null })
  useEffect(() => {
    let live = true
    set({ status: 'loading', row: null })
    load().then(row => { if (live) set({ status: 'ready', row }) })
      .catch(() => { if (live) set({ status: 'error', row: null }) })
    return () => { live = false }
  }, deps)
  return s
}

export default function MasterclassPage({ product }) {
  const { data, patch } = useProductData()
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const phone = usePhone()
  const personId = me.person.id

  const recording = data.recordings[0] || null
  const duration = data.content.find(c => c.recording_id === recording?.id)?.duration_seconds || 0
  const length = duration ? f.duration(duration) : ''
  const questionsEmail = product.questions_email || SITE.helpEmail

  // the video's link (only once it's released to them), the offer and whether the events were sent
  const content = useRead(() => (recording ? loadRecordingContent(recording.id) : Promise.resolve(null)), [recording?.id])
  const answer = useRead(() => loadOfferState(product.id), [product.id, personId])
  const watchEvents = sendsWatchEvents(product)
  const sentRead = useRead(() => (watchEvents ? loadSent(personId, product.id) : Promise.resolve(null)), [product.id, personId, watchEvents])

  // where they are: their saved row, and this visit's position and furthest point as it plays
  const row = recording ? progressFor(data, recording.id) : null
  const base = useRef(null)
  if (recording && base.current?.id !== recording.id) {
    base.current = { id: recording.id, furthest: row?.furthest_seconds || 0, watched: row?.seconds_watched || 0 }
  }
  const [pos, setPos] = useState(null)
  const [furthest, setFurthest] = useState(0)
  const view = progressView({ row, duration, live: pos == null ? null : { position: pos, furthest } })
  // the line under the name, from where they were when the page opened, so it doesn't change as they watch
  const first = useRef(null)
  if (recording && first.current === null) first.current = firstVisit(row, duration)

  // the player: hidden behind the cover until Play; a seek starts it (or moves it once it's playing)
  const [playing, setPlaying] = useState(false)
  const [seek, setSeek] = useState(null)
  const [coverLine, setCoverLine] = useState(null)
  const waiting = useRef(null)
  const vimeo = vimeoOf(content.row)
  function start(seconds) {
    if (content.status === 'loading') { waiting.current = seconds; return }
    if (!vimeo) { setCoverLine(content.status === 'error' ? COPY.loadFailed : COPY.notReady); return }
    setCoverLine(null)
    setSeek(s => ({ n: (s?.n || 0) + 1, seconds }))
  }
  useEffect(() => {
    if (content.status === 'loading' || waiting.current == null) return
    const seconds = waiting.current
    waiting.current = null
    start(seconds)
  }, [content.status])

  // the offer, the card or nothing; reached is the database's (every device) or this visit's
  const [reachedHere, setReachedHere] = useState(null)
  const reached = Boolean(data.state?.offer_reached_at || reachedHere)
  const area = offerArea({ status: answer.status, answer: answer.row, reached })
  const offerOn = area.kind === 'placeholder' || area.kind === 'offer'
  const at = offerOn ? revealSeconds(area.offer) : null

  // the events, each asked for at most once a visit, and only while it isn't sent for them yet (a
  // read that failed counts as not sent: the server sends each once whatever the page asks)
  const asked = useRef({ play: false, offer: false })
  const sent = sentRead.row || {}
  function send(event) {
    if (!watchEvents || viewingAs || asked.current[event]) return
    if (event === 'play' && sent.play_sent_at) return
    if (event === 'offer' && sent.offer_sent_at) return
    asked.current[event] = true
    sendEvent(event).catch(() => { /* the page shows none of its answers */ })
  }

  // play, the first time the video starts on this visit, once the page knows whether it was sent
  const [started, setStarted] = useState(false)
  useEffect(() => {
    if (started && sentRead.status !== 'loading') send('play')
  }, [started, sentRead.status])

  // offer, once it's reached (written to the database, here or on another device) and shown
  useEffect(() => {
    if (area.kind === 'offer' && data.state?.offer_reached_at && sentRead.status !== 'loading') send('offer')
  }, [area.kind, data.state?.offer_reached_at, sentRead.status])

  const writing = useRef(false)
  function reveal(how) {
    if (!offerOn || reached || writing.current) return
    setReachedHere(how)
    if (viewingAs) return
    writing.current = true
    markReached(personId, product.id, how)
      .then(stamp => {
        patch(d => ({
          ...d,
          state: { ...(d.state || { product_id: product.id }), offer_reached_at: stamp, offer_reached_how: how },
        }))
      })
      .catch(() => {
        // reached on another device first (the database keeps the first time), or offline: it shows
        // here all the same, and the next visit that reaches it writes it again
        writing.current = false
      })
  }
  function onTime(s) {
    setPos(s)
    setFurthest(x => (s > x ? s : x))
    if (at != null && s >= at) reveal('position')
  }

  function onReport(report) {
    if (viewingAs || !recording) return
    // the video ended: the offer shows, as on the watch page
    if (duration && report.furthest >= duration) reveal('ended')
    const payload = progressPayload(base.current || {}, report, duration)
    saveProgress(personId, recording.id, payload)
      .then(saved => {
        if (!saved) return
        patch(d => ({ ...d, progress: [...d.progress.filter(p => p.recording_id !== recording.id), saved] }))
      })
      .catch(() => { /* the next report tries again */ })
  }

  function onPlay() {
    setPlaying(true)
    setStarted(true)
  }

  const resume = view.resume
  const progressLine = (
    <div className="mc-line">
      <span className="mc-line-progress">
        <span className="mc-bar" aria-hidden="true"><span style={{ width: view.pctLabel }} /></span>
        {view.watchedLine}
      </span>
      <span className="mc-dot" aria-hidden="true" />
      <span className="mc-line-actions">
        <button type="button" className="mc-carry" onClick={() => start(pos != null ? pos : resume)}>{view.carryOn}</button>
        {/* only once there's a place to carry on from: before that it would do what Start watching does */}
        {view.canRestart && (
          <>
            <span className="mc-dot" aria-hidden="true" />
            <button type="button" className="mc-restart" onClick={() => start(0)}>{COPY.restart}</button>
          </>
        )}
      </span>
    </div>
  )

  return (
    <div className="mc">
      <section className="mc-head">
        <div className="mc-eyebrow">{COPY.eyebrow}</div>
        <h1 className="mc-h1">{product.name}</h1>
        <p className="mc-lede">{first.current ? COPY.ledeFirst(length) : COPY.lede(length)}</p>
      </section>

      <section className="mc-main">
        <div className="mc-video-col">
          <div className={playing ? 'mc-video mc-video--on' : 'mc-video'}>
            <div className="mc-video-player" inert={!playing}>
              <RecordingPlayer title={product.name} content={content.row} savedSeconds={row?.position_seconds || 0}
                seek={seek} onTime={onTime} onReport={onReport} onPlay={onPlay} canSave={!viewingAs}
                watchedShare={Number(data.product?.watched_share) || 0.9} phone={phone} />
            </div>
            {!playing && (
              <button type="button" className="mc-cover" aria-label={coverLabel(resume)} onClick={() => start(resume)}>
                <span className="mc-cover-eyebrow">{product.name}</span>
                <span className="mc-cover-foot">
                  <span className="mc-cover-words">
                    <span className="mc-cover-title">{COPY.coverTitle}</span>
                    <span className="mc-cover-sub">{coverLine || COPY.coverSub(length)}</span>
                  </span>
                  <PlayDisc />
                </span>
              </button>
            )}
            {coverLine && <span className="mc-sr" role="status">{coverLine}</span>}
          </div>
          {progressLine}
          {phone && <p className="mc-sideways">{COPY.sideways}</p>}
          <p className="mc-disclaimer">{COPY.disclaimer}</p>
        </div>

        <aside className="mc-side">
          <div className="mc-card">
            <div className="mc-card-head">{COPY.progressHead}</div>
            <div className="mc-card-pct"><span className="mc-card-big">{view.pctLabel}</span><span className="mc-card-word">{COPY.progressWatched}</span></div>
            <div className="mc-card-bar" aria-hidden="true"><span style={{ width: view.pctLabel }} /></div>
            <div className="mc-card-line">{view.carryOn}</div>
          </div>
          <div className="mc-card">
            <div className="mc-card-head">{COPY.questionsHead}</div>
            <p className="mc-card-text">{COPY.questions(questionsEmail)}</p>
          </div>
        </aside>
      </section>

      {area.kind === 'placeholder' && <OfferPlaceholder offer={area.offer} onShowNow={() => reveal('show_now')} viewingAs={viewingAs} />}
      {area.kind === 'card' && <OwnerCard card={area.card} />}
      {area.kind === 'offer' && <OfferBlock offer={area.offer} />}
    </div>
  )
}
